import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { shareReplay, map, switchMap, mergeMap, tap } from 'rxjs/operators';
import { ApartmentInfoModel } from '../../../registrations-data/models/data/apartment-info.model';
import { RegistrationModel } from '../../../registrations-data/models/data/registration.model';
import { CodeDescriptionModel } from 'src/app/models/code-description.model';
import { RegStatusCode } from '../../enums/registration-statuscode.enum';
import { RegistrationResponseByRegistrationIdModel } from 'src/app/registrations-data/models/response/registrations-response.model';

@Injectable({
  providedIn: 'root',
})
export class NewRegistrationDataCollectionService {

  baseUrlReg = environment.baseUrlRegistration;
  // ***global variables used to store STATE MANAGEMENT; DO NOT DELETE;
  stabCount: number;
  permExcemptCount: number;
  previousReason: any;
  showDiff: boolean;
  newBieView: boolean;
  referenceNumber: string;
  latestApprovedRegistrationId: string;
  supplementalRegistrationIsStillPending: boolean;
  registrationTypeCode: string;
  setRegistrationStatusReqForMoreInfo: boolean;
  hutsBuildingId: number;
  // *****global variables used to store STATE MANAGEMENT; DO NOT DELETE;
  private selectedTransactionGUID: string = null;
  private selectedTransactionGUIDSubject = new BehaviorSubject<string>(this.selectedTransactionGUID);
  selectedTransactionGUID$ = this.selectedTransactionGUIDSubject.asObservable();

  // Here locally to set the subject as well as an easy way to get at other children in a utility manner
  private registrationForm: RegistrationModel = null;
  regSourceRegID: string;
  private freshRegistrationInProgressModelCache$;
  private formBehavior = new BehaviorSubject<any>(this.registrationForm);
  regFormObserv: Observable<RegistrationModel> = this.formBehavior.asObservable();

  private menuIndexSubject = new BehaviorSubject<number>(0);
  menuIndexObserv$: Observable<number> = this.menuIndexSubject.asObservable();

  // tslint:disable-next-line: max-line-length
  // This helps the data collection service make a stream of where in the overall form the user is with a specific apartment.  The selected APT is the index of apt list
  private selectedApt = 0;
  private selectedAptSubject = new BehaviorSubject<number>(this.selectedApt);
  selectedApt$: Observable<number> = this.selectedAptSubject.asObservable();
  //
  private syncInfoSubject = new Subject();
  // creating an observable syncInfo$
  syncInfo$: Observable<any> = this.syncInfoSubject.asObservable();

  selectedApartment$: Observable<ApartmentInfoModel> = this.selectedApt$.pipe(
    switchMap((aptIndex) =>
      this.regFormObserv.pipe(
        map((reg) => {
          if (reg && reg.building && reg.apartments) {
            return reg.apartments[aptIndex];
          } else {
            return new ApartmentInfoModel();
          }
        })
      )
    )
  );

  get freshRegistrationInProgressModel$() {
    this.freshRegistrationInProgressModelCache$ = null;
    if (!this.freshRegistrationInProgressModelCache$) {
      this.freshRegistrationInProgressModelCache$ = this.selectedTransactionGUID$.pipe(
        switchMap((guid) => {
          if (!guid) {
            return null;
          }
          return this.httpClient
            .get(this.baseUrlReg + '/' + guid)
            .pipe(
              map((value: any) => {
                const TxJsonPayload = JSON.parse(value.data);
                const reg: RegistrationModel = TxJsonPayload;
                // reg.primaryKey = value.transactionId;
                // reg.building = TxJsonPayload.building;
                // reg.registrationType = value.registrationType;
                // reg.registrationType = new CodeDescriptionModel({
                //   code: TxJsonPayload.registrationType.code,
                //   description: 'Initial Registrtaion for a building'
                // });

                reg.registrationStatus = new CodeDescriptionModel({
                  code: RegStatusCode.INPROGRESS,
                  description: 'IN PROGRESS',
                  shortName: 'In Progress'
                });
                // tslint:disable-next-line: max-line-length
                // this should actually be mapped from a json docs array that this api call gives us into the local data model called attachments since they aren't exactly the same
                // const docs: any[] = value.jsonDocuments;
                // reg.documents = new Array<Document>();
                // When clicking on view button for downloading the document, we are calling an API to get the docs
                // from the server and pushing them in to local reg.attachments array. Howerver  we are pushing the
                // same docs again in to the same local reg.attachments array.
                // So commented the following lines of code for resolving defect:1136

                // docs.forEach((element, index) => {
                //   const attachment = new Attachment();

                //   attachment.fileName = element.fileName;
                //   attachment.guid = element.id;
                //   attachment.type = element.typeCode;
                //   reg.attachments.push(attachment);
                //   console.log('element: ' + index, element);
                // });

                // if (TxJsonPayload.attachments) {
                //   TxJsonPayload.attachments.forEach((attachment) => {
                //     reg.documents.push(attachment);
                //   });
                // }
                // reg.registrationSummary.reasons = TxJsonPayload.reasons;
                // reg.otherRegistrationForRevisionReason =
                //   TxJsonPayload.otherregistrationupdatereason;
                // reg. = TxJsonPayload.addOnTypeCode;
                // reg.addOnType = TxJsonPayload.addOnType;
                return reg;
              })
            );
        }),
        shareReplay(1)
      );
    }

    return this.freshRegistrationInProgressModelCache$;
  }

  navigation: string;

  constructor(private httpClient: HttpClient, private toast: ToastrService) { }

  setRegistration(reg: RegistrationModel) {
    this.registrationForm = reg;
    this.formBehavior.next(reg);
  }
  setMenuIndex(index: number) {
    this.menuIndexSubject.next(index);
  }

  // get the registration by passing regId
  getRegistrationDetailsByRegId(registrationId: string): Observable<any> {
    return this.httpClient.get<any>(this.baseUrlReg + '/' + registrationId
    + '/apartments').pipe(shareReplay(1));
  }

  // get the registrations comments by passing regId & commentId
  getRegistrationCommentsByCommentsId(regId: string) {
    const url = `${this.baseUrlReg}/${regId}/activities/CreatedComment/comments`;
    return this.httpClient.get(url).pipe(
      mergeMap((comments: any) => this.httpClient.
      get(`${this.baseUrlReg}/${regId}/comments/${comments[0].commentId}`).pipe())
    );
  }

  sync(latestRegistration: RegistrationModel, registrationId: string) {
    if (registrationId !== null) {
        latestRegistration.transactionId = registrationId;
      } else {
        registrationId = latestRegistration.transactionId;
     }

    const transactionObj = {
      data: JSON.stringify(latestRegistration),
      registrationId: latestRegistration.transactionId,
      buildingId: latestRegistration.building.buildingId,
      registrationTypeCode: latestRegistration.registrationType.code || 'IR',
      isDraft: true,
    };

    // If the app is in a status to sync back to payload then do so... AKA "In Progress"
    if (latestRegistration.registrationStatus &&
        latestRegistration.registrationStatus.code === RegStatusCode.INPROGRESS) {
      // if the save to database is ok then propogate the new data model to all the observervers
      // if the transaction id given was blank then we need to put it into the data model when it comes back from the web service
      this.httpClient.put<RegistrationModel>(this.baseUrlReg + '/' + latestRegistration.transactionId, transactionObj).subscribe(
        (data: any) => {
          // latestRegistration.primaryKey= data.transactionId;
          this.setRegistration(latestRegistration);
          this.syncInfoSubject.next();
        },
        (err: any) => console.log('error saving')
      );
    }
  }

  setSelectedApt(selectedAptIndex: number) {
    if (this.registrationForm && this.registrationForm.building &&
      Array.isArray(this.registrationForm.apartments) && this.registrationForm.apartments.length - 1 >= selectedAptIndex) {
      this.selectedAptSubject.next(selectedAptIndex);
    }
  }

  setSelectedTransactionGUID(guid: string) {
    if (guid) {
      this.selectedTransactionGUIDSubject.next(guid);
    }
    this.selectedTransactionGUID = guid;
  }

  submit(callback?: any) {
    let getSourceRegistrationId = null;
    if (this.registrationForm.registrationType.code !== 'IR') {
      getSourceRegistrationId = this.regSourceRegID;
    }
    this.registrationForm.registrationStatus = {code: 'Submitted', description: 'Submitted', shortName: 'Submitted'};
    const transactionObj = {
      data: JSON.stringify(this.registrationForm),
      registrationId: this.registrationForm.transactionId,
      buildingId: this.registrationForm.building.buildingId,
      registrationTypeCode: this.registrationForm.registrationType.code || 'IR',
      isDraft: false,
      SourceRegistrationId: getSourceRegistrationId,
    };
    this.httpClient.put<RegistrationModel>(this.baseUrlReg + '/' + this.registrationForm.transactionId, transactionObj).
      subscribe((data: any) => {
        if (callback) {
          callback();
        }
      },
        (err: any) => {
          this.toast.error('Submit Unsuccessful!!. Please try again. Or Please contact system administrator.');
        }
      );
  }
  isAddon() {
    return (
      this.registrationForm && this.registrationForm.registrationType.code === 'AO'
    );
  }
  is_NRA_PVA() {
    return (
      this.registrationForm && (this.registrationForm.registrationType.code === 'NRA' ||
        this.registrationForm.registrationType.code === 'PVA')
    );
  }
  is_NRA_RCRS_PVA() {
    return (
      this.registrationForm && (this.registrationForm.registrationType.code === 'NRA'
        || this.registrationForm.registrationType.code === 'RCRS' ||
        this.registrationForm.registrationType.code === 'PVA')
    );
  }
  isRCRS() {
    return (
      this.registrationForm && this.registrationForm.registrationType.code === 'RCRS'
    );
  }

  save(registrationsResponse: RegistrationResponseByRegistrationIdModel) {
    let getSourceRegistrationId = null;
    if (registrationsResponse.registrationType.code !== 'IR') {
      getSourceRegistrationId = this.regSourceRegID;
    }
    const transactionObj = {
      data: JSON.stringify(this.registrationForm),
      registrationId: registrationsResponse.registrationId,
      buildingId: registrationsResponse.buildingId,
      // incidentTypeCode: this.registrationForm.registrationType || 'IR',
      registrationTypeCode: registrationsResponse.registrationType.code || 'IR',
      isDraft: true,
      SourceRegistrationId: getSourceRegistrationId
    };

    // If the app is in a status to sync back to payload then do so... AKA "In Progress"
    if (this.registrationForm.registrationStatus &&
      this.registrationForm.registrationStatus.code === RegStatusCode.INPROGRESS) {
      // if the save to database is ok then propogate the new data model to all the observervers
      // if the transaction id given was blank then we need to put it into the data model when it comes back from the web service
      this.httpClient
        .put<RegistrationModel>(this.baseUrlReg + '/' + registrationsResponse.registrationId, transactionObj)
        .subscribe(
          // tslint:disable-next-line: max-line-length
          (data: any) => {
            // this.registrationForm.primaryKey = data.transactionId;
            this.syncInfoSubject.next();
            this.setRegistration(this.registrationForm);
          },
          (err: any) => {
            console.log('error saving');
          }
        );
    }
  }

  start(latestRegistration: RegistrationModel, cb = null) {
    const transactionObj = {
      data: JSON.stringify(latestRegistration),
      sourceRegistrationId: null,
      registrationTypeCode: latestRegistration.registrationType.code,
      buildingId: latestRegistration.building.buildingId,
      isDraft: true,
    };
    this.httpClient
      .post<RegistrationModel>(this.baseUrlReg, transactionObj)
      .subscribe(
        (data: any) => {
          latestRegistration.transactionId = data.registrationId;
          if (cb && data && data.registrationId) {
            cb(data.registrationId);
          } else if (latestRegistration) {
            this.setRegistration(latestRegistration);
          }
          this.referenceNumber = data.referenceNumber;
          this.setSelectedTransactionGUID(data.registrationId);
        },
        (err: any) => console.log('error saving')
      );
  }

  // start New IR or AMD/NRA/PVA/RCRS
  startRegistration(latestRegistration: RegistrationModel): Observable<RegistrationResponseByRegistrationIdModel> {
    const transactionObj = {
      data: JSON.stringify(latestRegistration),
      sourceRegistrationId: null,
      registrationTypeCode: latestRegistration.registrationType.code,
      buildingId: latestRegistration.building.buildingId,
      isDraft: true,
    };
    return this.httpClient.post<RegistrationResponseByRegistrationIdModel>(this.baseUrlReg, transactionObj).pipe(
      shareReplay(1));
  }
}
